import { useEffect, useState } from 'react';
import { GraduationCap, Users, Clock } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Grid from '../ui/Grid';
import {
    listRoleRequests,
    type RoleRequest,
    type RoleRequestType,
} from '../../mentorshipService';
import type { BackendUserWithPlan } from '../../userService';
import RoleRequestModal from './RoleRequestModal';
import './RoleOnboardingSplash.css';
import './mentorship.css';

/**
 * RoleOnboardingSplash — shown when the user has `role===null` and explicitly
 * navigates into the mentorship dashboard (via the "Want to join?" banner on
 * the legacy course list, or by direct deep link). The default Coaching
 * sub-tab still falls through to the existing course list for null-role
 * users; this splash is opt-in.
 */

type Props = {
    user: BackendUserWithPlan;
};

function RoleOnboardingSplash({ user }: Props) {
    const [pendingRequest, setPendingRequest] = useState<RoleRequest | null>(null);
    const [loading, setLoading] = useState(true);
    const [modalType, setModalType] = useState<RoleRequestType | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const page = await listRoleRequests('pending');
                if (!cancelled) {
                    const own = page.results.find((r) => r.user === user.id) ?? null;
                    setPendingRequest(own);
                }
            } catch (err) {
                console.error('[RoleOnboardingSplash] failed to load role requests', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [user.id]);

    const handleRequestSubmitted = (request: RoleRequest) => {
        setPendingRequest(request);
        setModalType(null);
    };

    return (
        <section className="mentorship-view">
            <header className="mentorship-view__header">
                <div>
                    <h2 className="mentorship-view__title">Coaching</h2>
                    <p className="mentorship-view__subtitle">
                        Join the mentorship program to track your growth across the framework.
                    </p>
                </div>
            </header>

            <div className="mentorship-view__content">
                {loading ? (
                    <p className="role-splash__loading">Loading…</p>
                ) : pendingRequest ? (
                    <Card variant="row" className="role-splash__pending">
                        <Card.Header>
                            <Clock size={24} />
                            <div>
                                <h3 className="role-splash__pending-title">
                                    Your application is pending review
                                </h3>
                                <p className="role-splash__pending-body">
                                    An admin will approve or reject your{' '}
                                    {pendingRequest.request_type === 'become_mentor'
                                        ? 'mentor'
                                        : 'mentee'}{' '}
                                    application shortly. You'll receive a notification when
                                    the decision is made.
                                </p>
                            </div>
                        </Card.Header>
                    </Card>
                ) : (
                    <Grid minItemWidth={280} gap="lg">
                        <Card variant="stacked" className="role-splash__cta">
                            <Card.Header>
                                <GraduationCap size={28} />
                                <h3 className="role-splash__cta-title">
                                    I want to be mentored
                                </h3>
                            </Card.Header>
                            <Card.Body>
                                <p className="role-splash__cta-body">
                                    Get matched with a senior staffer who reviews your work
                                    every quarter, scores you against the framework, and gives
                                    actionable feedback for growth.
                                </p>
                            </Card.Body>
                            <Card.Footer>
                                <Button
                                    variant="primary"
                                    size="md"
                                    fullWidth
                                    onClick={() => setModalType('become_mentee')}
                                >
                                    Apply as mentee
                                </Button>
                            </Card.Footer>
                        </Card>

                        <Card variant="stacked" className="role-splash__cta">
                            <Card.Header>
                                <Users size={28} />
                                <h3 className="role-splash__cta-title">
                                    I want to mentor others
                                </h3>
                            </Card.Header>
                            <Card.Body>
                                <p className="role-splash__cta-body">
                                    Share your expertise. Review quarterly framework
                                    submissions from junior staff, set the bar, and help
                                    them level up.
                                </p>
                            </Card.Body>
                            <Card.Footer>
                                <Button
                                    variant="primary"
                                    size="md"
                                    fullWidth
                                    onClick={() => setModalType('become_mentor')}
                                >
                                    Apply as mentor
                                </Button>
                            </Card.Footer>
                        </Card>
                    </Grid>
                )}
            </div>

            {modalType && (
                <RoleRequestModal
                    requestType={modalType}
                    onClose={() => setModalType(null)}
                    onSubmitted={handleRequestSubmitted}
                />
            )}
        </section>
    );
}

export default RoleOnboardingSplash;
